// ============================================================================
//  Templates.js — Templates department entry point.
// ----------------------------------------------------------------------------
//  Two views, toggled by local state (no nested route needed):
//    • list  — searchable grid of conditions + "New condition" button
//    • detail — one condition + tabs for each child library
//  Kept in one component tree so back-navigation is instant (no re-fetch).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  DeptIcon, IconPlus, IconSparkle, IconEdit, IconTrash,
  IconStethoscope, IconWard,
} from './icons';
import { templateConditions, templateCondition } from '../../BaseURL';
import notify from './notify';
import ConditionDetail from './ConditionDetail';
import ConditionEditor from './ConditionEditor';
import './Templates.css';

const AGE_FILTERS = [
  { id: '',           label: 'All ages' },
  { id: 'paediatric', label: 'Paediatric (≤12)' },
  { id: 'adult',      label: 'Adult (>12)' },
];

// Care-setting is now conveyed by the sidebar sub-tab, so the dropdown filter
// is removed. Same condition can exist as separate rows in both libraries
// (schema uniqueness is (source, age_scope, care_setting)), so this split is
// purely a browsing convenience.
const CARE_LIBRARIES = [
  {
    key:      'library-opd',
    setting:  'outpatient',
    label:    'Library · Outpatient',
    heading:  'Outpatient library',
    subtitle: 'Conditions that can be managed and sent home from OPD.',
  },
  {
    key:      'library-ipd',
    setting:  'inpatient',
    label:    'Library · Inpatient',
    heading:  'Inpatient library',
    subtitle: 'Conditions that need admission and full-scope documentation.',
  },
];

const SOURCE_FILTERS = [
  { id: '',          label: 'Any source' },
  { id: 'diagnosis', label: 'Diagnosis' },
  { id: 'procedure', label: 'Procedure' },
];

const CATEGORY_FILTERS = [
  { id: '',            label: 'All categories' },
  { id: 'infectious',  label: 'Infectious' },
  { id: 'cardiovascular', label: 'Cardiovascular' },
  { id: 'respiratory', label: 'Respiratory' },
  { id: 'obgyn',       label: 'OB/GYN' },
  { id: 'paediatric',  label: 'Paediatric' },
  { id: 'trauma',      label: 'Trauma' },
  { id: 'chronic',     label: 'Chronic / NCD' },
  { id: 'other',       label: 'Other' },
];

export default function Templates({ def }) {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [ageScope, setAgeScope] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [openId, setOpenId] = useState(null);
  // `editing` is either null, 'new-outpatient', 'new-inpatient', or a condition
  // row. The 'new-*' sentinels let ConditionEditor pre-select the care setting
  // that matches the sub-tab the user was on when they clicked "New template".
  const [editing, setEditing] = useState(null);

  const loadConditions = useCallback(async () => {
    setLoading(true);
    try {
      // No careSetting filter — we load everything once and partition into
      // outpatient / inpatient sub-tabs client-side. Templates with
      // care_setting='both' appear in both sub-tabs.
      const { data } = await axios.get(templateConditions, {
        params: { search, category, ageScope, sourceType },
      });
      setConditions(data.conditions || []);
    } catch (err) {
      notify.error('Failed to load conditions', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [search, category, ageScope, sourceType]);

  useEffect(() => { loadConditions(); }, [loadConditions]);

  const openCondition = (c) => setOpenId(c.id);
  const backToList = () => setOpenId(null);

  const openCondObj = useMemo(
    () => conditions.find((c) => c.id === openId),
    [openId, conditions]
  );

  // Client-side partition. A condition with care_setting='both' shows up in
  // both libraries — one row, indexed twice — which matches how the same
  // condition can be treated either way.
  const filterFor = (setting) => conditions.filter(
    (c) => c.care_setting === setting || c.care_setting === 'both'
  );

  const handleDelete = async (c) => {
    const ok = await notify.confirm('Delete condition?', `${c.name} and every pick-list under it will be removed.`, {
      confirmText: 'Delete', cancelText: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(templateCondition(c.id));
      notify.toast.success('Condition deleted');
      loadConditions();
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const items = CARE_LIBRARIES.map(({ key, setting, label, heading, subtitle }) => ({
    key,
    label,
    icon: setting === 'outpatient' ? <IconStethoscope size={18} /> : <IconWard size={18} />,
    render: () => (
      openId ? (
        <ConditionDetail
          condition={openCondObj}
          conditionId={openId}
          onBack={backToList}
          onChanged={loadConditions}
        />
      ) : (
        <LibraryList
          heading={heading}
          subtitle={subtitle}
          careSetting={setting}
          conditions={filterFor(setting)}
          loading={loading}
          search={search}
          setSearch={setSearch}
          category={category} setCategory={setCategory}
          ageScope={ageScope} setAgeScope={setAgeScope}
          sourceType={sourceType} setSourceType={setSourceType}
          onOpen={openCondition}
          onNew={() => setEditing(`new-${setting}`)}
          onEdit={setEditing}
          onDelete={handleDelete}
        />
      )
    ),
  }));

  // Turn the 'new-outpatient' / 'new-inpatient' sentinels into a real editor
  // payload so the editor pre-selects the care setting from the sub-tab.
  const editorInitial =
    editing === 'new-outpatient' ? null
      : editing === 'new-inpatient' ? null
      : editing;
  const defaultCareSetting =
    editing === 'new-outpatient' ? 'outpatient'
      : editing === 'new-inpatient' ? 'inpatient'
      : null;

  // Sub-tab state — replaces the sidebar entries the standalone project had.
  const [libTab, setLibTab] = useState(0);
  const activeLib = items[libTab] || items[0];

  return (
    <>
      <div className="tp-subtabs">
        {items.map((it, i) => (
          <button
            key={it.key}
            type="button"
            className={`tp-subtab ${i === libTab ? 'is-active' : ''}`}
            onClick={() => { setLibTab(i); setOpenId(null); }}
          >
            {it.icon}
            <span>{it.label}</span>
          </button>
        ))}
      </div>
      {activeLib?.render()}
      {editing && (
        <ConditionEditor
          initial={editorInitial}
          defaultCareSetting={defaultCareSetting}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadConditions(); }}
        />
      )}
    </>
  );
}

/* ── The list view ─────────────────────────────────────────────────────── */
function LibraryList({
  heading, subtitle, careSetting,
  conditions, loading, search, setSearch,
  category, setCategory, ageScope, setAgeScope,
  sourceType, setSourceType,
  onOpen, onNew, onEdit, onDelete,
}) {
  return (
    <div className="tp-page">
      {(heading || subtitle) && (
        <div className="tp-list-head">
          <div className="tp-list-head-text">
            {heading && <h2 className="tp-list-heading">{heading}</h2>}
            {subtitle && <p className="tp-list-subtitle">{subtitle}</p>}
          </div>
          <span className={`tp-list-count ${careSetting === 'inpatient' ? 'is-ipd' : 'is-opd'}`}>
            {conditions.length} template{conditions.length === 1 ? '' : 's'}
          </span>
        </div>
      )}
      <div className="tp-toolbar">
        <input
          className="tp-search"
          placeholder="Search by condition name, ICD-10 or G-DRG code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="tp-select" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
          {SOURCE_FILTERS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select className="tp-select" value={ageScope} onChange={(e) => setAgeScope(e.target.value)}>
          {AGE_FILTERS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        <select className="tp-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORY_FILTERS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button className="tp-btn tp-btn-primary" onClick={onNew}>
          <IconPlus size={16} /> New {careSetting === 'inpatient' ? 'inpatient' : 'outpatient'} template
        </button>
      </div>

      {loading && <div className="tp-empty">Loading…</div>}

      {!loading && conditions.length === 0 && (
        <div className="tp-empty">
          <div className="tp-empty-icon"><IconSparkle size={28} /></div>
          <h3>No templates yet</h3>
          <p>Create your first template — pick a Diagnosis or Procedure from the imported NHIS booklet.</p>
          <button className="tp-btn tp-btn-primary" onClick={onNew}>
            <IconPlus size={16} /> Add first template
          </button>
        </div>
      )}

      {!loading && conditions.length > 0 && (
        <div className="tp-grid">
          {conditions.map((c) => (
            <button
              key={c.id}
              type="button"
              className="tp-card"
              onClick={() => onOpen(c)}
            >
              <div className="tp-card-head">
                <span className="tp-card-name">{c.name}</span>
                <div className="tp-card-pills">
                  <SourcePill condition={c} />
                  <AgePill scope={c.age_scope} />
                </div>
              </div>
              <div className="tp-card-meta">
                {c.source_code && <span className="tp-chip">{c.source_code}</span>}
                <CareSettingPill setting={c.care_setting} />
                {c.category && <span className="tp-chip">{c.category}</span>}
                <span className="tp-chip tp-chip-muted">Used {c.use_count}×</span>
              </div>
              <div className="tp-card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="tp-icon-btn" onClick={() => onEdit(c)} title="Edit">
                  <IconEdit size={14} />
                </button>
                <button className="tp-icon-btn danger" onClick={() => onDelete(c)} title="Delete">
                  <IconTrash size={14} />
                </button>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// SourcePill accepts either a condition row (preferred) or a raw type string
// for back-compat. When given a row, prefer `pick_origin` (which tab the user
// picked from) over `source_type` (the technical anchor). This means a
// diagnosis drill-down that anchored on a G-DRG variant still shows
// "Diagnosis" — matching what the user actually chose.
export function SourcePill({ condition, type }) {
  const value = condition?.pick_origin || condition?.source_type || type;
  const map = {
    diagnosis: { label: 'Diagnosis', cls: 'tp-pill-src-dx' },
    procedure: { label: 'Procedure', cls: 'tp-pill-src-proc' },
    manual:    { label: 'Manual',    cls: 'tp-pill-both' },
  };
  const p = map[value] || map.manual;
  return <span className={`tp-pill ${p.cls}`}>{p.label}</span>;
}

export function CareSettingPill({ setting }) {
  if (!setting) return null;
  const label = setting === 'inpatient' ? 'Inpatient'
              : setting === 'outpatient' ? 'Outpatient'
              : 'IP + OP';
  return <span className="tp-chip">{label}</span>;
}

export function AgePill({ scope }) {
  const map = {
    paediatric: { label: 'Paediatric', cls: 'tp-pill-paed' },
    adult:      { label: 'Adult',      cls: 'tp-pill-adult' },
    both:       { label: 'Both',       cls: 'tp-pill-both' },
  };
  const p = map[scope] || map.both;
  return <span className={`tp-pill ${p.cls}`}>{p.label}</span>;
}
